import React, { useEffect, useRef, useState } from 'react'
import { useBetContext } from '../../../../../context/BetContext.tsx';
import { BsBroadcast } from 'react-icons/bs';
import { IoMdClose } from 'react-icons/io';
import { useUser } from '../../../../../context/UserContext.tsx';
import { BetDetails } from '../../../../../models/MyBetModel.tsx';
import { Odd } from '../../../../../models/Odd_response_model.tsx';
import { calcProfit, formatNumbers, calcStake, shouldShowDelayedBetMessage, getBetPrimaryName, getWagerLimitForBet, getBetSpecialtyMarketLabel, isLiveBet, formatSpecialtyMarketLabel, formatNumber } from '../../../../../helpers/functions.tsx';
import { useModal } from '../../../../../context/ModalContext.tsx';
import BetLogo from '../../BetLogo.tsx';
import StraightBets from '../StraightBets.tsx';
import '../../../../../styles/StraightQuickSlip.css';
interface StraightBetsResponsiveProps {
    setShowLimitAlert?: (value: boolean) => void;
    setErrorMessage?: (value: string) => void;
    onPitcherChange?: (gameId: string, team: "away" | "home") => void;
    onBetConfirmation?: () => void;
}

function formatQuickSlipLine(
    odd?: Odd | { points?: number | null; price?: number | null; selection_line?: string | null; market_id?: string | null } | null,
    marketName?: string | null,
) {
    if (!odd) return "";
    const isMoneyline =
        String(marketName ?? "").toLowerCase().includes("moneyline") ||
        String(odd.market_id ?? "").toLowerCase() === "moneyline";
    const price = odd.price != null ? formatNumber(odd.price) : "";
    if (isMoneyline || odd.points == null) return price;

    const line = String(odd.selection_line ?? "").toLowerCase();
    const points = formatNumber(odd.points, undefined, "points");
    if (line === "over" || line.startsWith("over")) return `O ${odd.points} (${price})`.trim();
    if (line === "under" || line.startsWith("under")) return `U ${odd.points} (${price})`.trim();
    return `${points} (${price})`;
}

const DEFAULT_QUICK_BETS = [100, 250, 500, 700];

function roundQuickAmount(value: number) {
    return Math.round(value / 5) * 5;
}

function buildQuickBets(wagerLimit: number): number[] {
    const maxAmount = Math.max(0, Math.round(Number(wagerLimit) || 0));
    if (maxAmount <= 0) return DEFAULT_QUICK_BETS;

    const amounts = [
        roundQuickAmount(Math.floor(maxAmount / 8)),
        roundQuickAmount(Math.floor(maxAmount / 4)),
        roundQuickAmount(Math.floor(maxAmount / 2)),
        maxAmount,
    ].filter((amount, index, list) => amount > 0 && list.indexOf(amount) === index);

    if (amounts[amounts.length - 1] !== maxAmount) amounts.push(maxAmount);
    return amounts.slice(-4);
}

const StraightBetsResponsive = (props: StraightBetsResponsiveProps) => {
    const { setShowLimitAlert, setErrorMessage, onBetConfirmation } = props;
    const { bets, updateBet, clearBets, useFreePlay, setUseFreePlay } = useBetContext();
    const { user } = useUser();
    const latestBet = bets?.filter(bet => bet?.betDate?.seconds !== undefined).slice().sort((a, b) => (b.betDate?.seconds - a.betDate?.seconds))[0];
    const [betAmount, setBetAmount] = useState('');
    const [localPitchers, setLocalPitchers] = useState<{ home: boolean; away: boolean }>({
        home: latestBet?.pitchers?.home ?? false,
        away: latestBet?.pitchers?.away ?? false
    });
    const [selectedOddId, setSelectedOddId] = useState<string | undefined>(
        latestBet?.odd?.id ??
        latestBet?.odds?.find(o => o.is_main)?.id ??
        latestBet?.odds?.[0]?.id
    );
    const selectRef = useRef<HTMLSelectElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { closeModal, updateModalContent } = useModal();
    const shouldShowDelayedMessage = shouldShowDelayedBetMessage(latestBet);
    const isLive = isLiveBet(latestBet);

    useEffect(() => {
        if (latestBet?.odd?.id) {
            setSelectedOddId(latestBet.odd.id);
        } else if (latestBet?.odds?.length) {
            const mainOdd = latestBet.odds.find(o => o.is_main)?.id ?? latestBet.odds[0].id;
            setSelectedOddId(mainOdd);
        }
    }, [latestBet]);

    useEffect(() => {
        if (latestBet?.pitchers) {
            setLocalPitchers({
                home: latestBet.pitchers.home,
                away: latestBet.pitchers.away
            });
        }

    }, [latestBet]);

    // const togglePitcher = (team: "home" | "away") => {
    //     const updated = { ...localPitchers, [team]: !localPitchers[team] };
    //     setLocalPitchers(updated);
    //     if (latestBet) onPitcherChange?.(latestBet.gameId, team);
    // };

    const handleBetClick = (amount: number) => {
        setBetAmount(String(amount));

        if (!latestBet || !selectedOddId) return;

        const selectedOdd = oddsList.find(o => o.id === selectedOddId);
        if (!selectedOdd) return;

        let betValue = 0;
        let rewardValue = 0;

        if (selectedOdd.price > 0) {
            betValue = amount;
            rewardValue = calcProfit(betValue, selectedOdd.price);
        } else {
            rewardValue = amount;
            betValue = calcStake(rewardValue, selectedOdd.price);
        }

        updateBet({
            ...latestBet,
            bet: betValue,
            reward: rewardValue
        });
    };

    const handleConfirmBet = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, openNextModal: boolean) => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            if (!user?.userName) {
                setShowLimitAlert?.(true);
                setErrorMessage?.('Please login to place a bet.');
                closeModal();
                return;
            }

            const numericAmount = Number(betAmount);
            if (!latestBet) {
                setShowLimitAlert?.(true);
                setErrorMessage?.('No bet selected.');
                return;
            }

            if (!betAmount || numericAmount < 10) {
                setShowLimitAlert?.(true);
                setErrorMessage?.('Please enter a valid amount greater than $10');
                e.preventDefault();
                return;
            }
            let totalAvailable = useFreePlay ? (user?.freePlay || 0) : (user?.available || 0);


            if (useFreePlay) {
                if (numericAmount > totalAvailable) {
                    setShowLimitAlert?.(true);
                    setErrorMessage?.(
                        `The maximum amount to bet in Free Play is $${totalAvailable.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })}`
                    );
                    e.preventDefault();
                    return;
                }
            }

            const selectedOdd = oddsList.length === 1
                ? oddsList[0]
                : oddsList.find(o => o.id === selectedOddId) ?? latestBet.odd;


            if (!selectedOdd) return;

            const value = Number(betAmount);
            const price = selectedOdd.price;

            let stake = 0;
            let reward = 0;

            if (price > 0) {
                stake = value;
                reward = calcProfit(stake, price);
            } else {
                reward = value;
                stake = calcStake(reward, price);
            }

            const newBet: BetDetails = {
                ...latestBet,
                bet: Math.round(stake),
                reward: Math.round(reward),
                odd: selectedOdd,
                pitchers: {
                    home: localPitchers.home,
                    away: localPitchers.away,
                    names: latestBet.pitchers?.names ?? { home: '', away: '' }
                }
            };

            updateBet(newBet);

            if (openNextModal) {
                if (bets.length > 1) {
                    updateModalContent(
                        <StraightBets
                            setShowLimitAlert={setShowLimitAlert}
                            setErrorMessage={setErrorMessage}
                        />
                    );
                } else {
                    onBetConfirmation?.();
                }
            } else {
                closeModal();
            }

        } finally {
            setIsSubmitting(false);

        }
    };

    // const formatPitcherName = (name?: string): string => {
    //     if (!name) return "";
    //     const parts = name.split(" ");
    //     if (parts.length === 1) return name;
    //     const firstInitial = parts[0][0];
    //     const lastName = parts.slice(1).join(" ");
    //     return `${firstInitial} ${lastName}`;
    // };

    useEffect(() => {
        if (!selectRef.current) return;

        const select = selectRef.current;
        const selectedOption = select.options[select.selectedIndex];

        const temp = document.createElement("span");
        temp.style.visibility = "hidden";
        temp.style.position = "absolute";
        temp.style.whiteSpace = "nowrap";
        temp.style.font = getComputedStyle(select).font;
        temp.textContent = selectedOption.text;

        document.body.appendChild(temp);

        const width = Math.ceil(temp.offsetWidth) + 40;
        select.style.width = `${Math.max(width, 88)}px`;

        document.body.removeChild(temp);
    }, [selectedOddId]);

    if (!latestBet) {
        return null;
    }

    const oddsList = Array.isArray(latestBet.odds) && latestBet.odds.length > 0
        ? latestBet.odds : latestBet.odd ? [latestBet.odd] : [];

    const selectedOddForLimit =
        oddsList.find(o => o.id === selectedOddId) ??
        latestBet.odd ??
        oddsList[0];

    const betForLimit = { ...latestBet, odd: selectedOddForLimit };
    const wagerLimit = user ? Number(getWagerLimitForBet(user, betForLimit) || 0) : 0;
    const quickBets = buildQuickBets(wagerLimit);


    const handleOddChange = (oddId: string) => {
        setSelectedOddId(oddId);

        const selectedOdd = oddsList.find(o => o.id === oddId);
        if (!selectedOdd || !latestBet) return;

        const numeric = Math.round(Number(betAmount)) || 0;

        let betValue = 0;
        let rewardValue = 0;

        if (selectedOdd.price > 0) {
            betValue = numeric;
            rewardValue = calcProfit(betValue, selectedOdd.price);
        } else {
            rewardValue = numeric;
            betValue = calcStake(rewardValue, selectedOdd.price);
        }

        updateBet({
            ...latestBet,
            odd: selectedOdd,
            betName: selectedOdd.name,
            bet: betValue,
            reward: rewardValue,
        });
    };


    const handleBetAmountChange = (value: string) => {
        if (!/^\d*$/.test(value)) return;

        setBetAmount(value);
        if (!latestBet || !selectedOddId) return;

        const selectedOdd = oddsList.find(o => o.id === selectedOddId);
        if (!selectedOdd) return;

        const numeric = Math.round(Number(value));

        let betValue = 0;
        let rewardValue = 0;

        if (selectedOdd.price > 0) {
            betValue = numeric;
            rewardValue = calcProfit(betValue, selectedOdd.price);
        } else {
            rewardValue = numeric;
            betValue = calcStake(rewardValue, selectedOdd.price);
        }

        updateBet({
            ...latestBet,
            bet: betValue,
            reward: rewardValue,
            betName: selectedOdd.name
        });
    };

    // const getName = (text?: string) => {
    //     if (!text) return "";

    //     const words = text.trim().split(/\s+/);
    //     const overIndex = words.findIndex(w => w === "Over" || w === "Under");

    //     if (overIndex > 0) {
    //         return words[overIndex - 1];
    //     }

    //     return words.pop();
    // };

    if (!user) return null;

    const pickCount = bets.length;
    const pickName = getBetPrimaryName(latestBet);
    const marketLabel =
        getBetSpecialtyMarketLabel(latestBet)
        || formatSpecialtyMarketLabel(latestBet.marketName || latestBet.odd?.market);
    const selectedAmount = Number(betAmount);

    const persistCurrentAmount = () => {
        if (!latestBet || !selectedOddId) return;
        const numericAmount = Number(betAmount);
        if (!betAmount || !Number.isFinite(numericAmount) || numericAmount <= 0) return;

        const selectedOdd = oddsList.find(o => o.id === selectedOddId) ?? latestBet.odd;
        if (!selectedOdd) return;

        const price = selectedOdd.price;
        let stake = 0;
        let reward = 0;
        if (price > 0) {
            stake = numericAmount;
            reward = calcProfit(stake, price);
        } else {
            reward = numericAmount;
            stake = calcStake(reward, price);
        }

        updateBet({
            ...latestBet,
            bet: Math.round(stake),
            reward: Math.round(reward),
            odd: selectedOdd,
            betName: selectedOdd.name,
            pitchers: {
                home: localPitchers.home,
                away: localPitchers.away,
                names: latestBet.pitchers?.names ?? { home: '', away: '' }
            }
        });
    };

    const chooseMore = () => {
        persistCurrentAmount();
        closeModal();
    };

    const clearAndClose = () => {
        clearBets();
        setUseFreePlay(false);
        closeModal();
    };

    return (
        <div className="sq-slip">
            <div className="sq-slip__header">
                <div className="sq-slip__header-left">
                    <span className="sq-slip__count">{pickCount}</span>
                    <h1 className="sq-slip__title">Bet Slip</h1>
                </div>
                <div className="sq-slip__header-actions">
                    <button type="button" className="sq-slip__clear" onClick={clearAndClose}>
                        Clear
                    </button>
                    <button type="button" className="sq-slip__close" onClick={chooseMore} aria-label="Close">
                        <IoMdClose size={25} />
                    </button>
                </div>
            </div>

            <div className="sq-slip__body">
                {latestBet && (
                    <div className="sq-slip__pick">
                        <BetLogo bet={latestBet} className="sport-logos-modal" />
                        <div className="sq-slip__pick-meta">
                            <span className="sq-slip__pick-name">{pickName}</span>
                            {marketLabel ? <span className="sq-slip__pick-market">{marketLabel}</span> : null}
                            {latestBet.gameType?.toLowerCase().includes("live") && (
                                <span className="fw-bold inter live-bet-label">
                                    Live
                                    <span className="live-text-dot" aria-hidden="true" />
                                </span>
                            )}
                        </div>
                        <div className="sq-slip__odds">
                            {!isLive && oddsList.length > 1 ? (
                                <select
                                    ref={selectRef}
                                    className="sq-slip__odds-box sq-slip__odds-select"
                                    value={selectedOddId ?? ""}
                                    onChange={(e) => handleOddChange(e.target.value)}
                                >
                                    {oddsList.slice().sort((a, b) => Number(a.points ?? 0) - Number(b.points ?? 0))
                                        .map((odd) => (
                                            <option key={odd.id} value={odd.id}>
                                                {formatQuickSlipLine(odd, latestBet.marketName)}
                                            </option>
                                        ))}
                                </select>
                            ) : (
                                <span className="sq-slip__odds-box">
                                    {formatQuickSlipLine(
                                        oddsList.find(o => o.id === selectedOddId) ?? latestBet.odd ?? oddsList[0],
                                        latestBet.marketName
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {isLive && (
                    <div className="live-betting-notice">
                        <span className="live-betting-notice-label">
                            <BsBroadcast className="live-betting-notice-icon" aria-hidden="true" />
                            LIVE BETTING
                        </span>
                        <span className="live-betting-notice-hint">Odds and lines may change</span>
                    </div>
                )}

                <div className="sq-slip__wager">
                    <div className="sq-slip__wager-title">Wager Amount</div>
                    <div className="sq-slip__amount">
                        <span className="sq-slip__currency">$</span>
                        <input
                            type="number"
                            inputMode="decimal"
                            className="sq-slip__amount-input"
                            placeholder="Enter amount..."
                            value={betAmount}
                            onChange={(e) => handleBetAmountChange(e.target.value)}
                        />
                    </div>
                    <div className="sq-slip__chips">
                        {quickBets.map((amount) => (
                            <button
                                type="button"
                                key={amount}
                                className={`sq-slip__chip${selectedAmount === amount ? " is-selected" : ""}`}
                                onClick={() => handleBetClick(amount)}
                            >
                                ${amount.toLocaleString("en-US")}
                            </button>
                        ))}
                    </div>
                </div>
                {(user?.freePlay ?? 0) > 0 && (
                    <label className="sq-slip__freeplay">
                        <input type="checkbox" className="checkbox-pitcher" checked={useFreePlay} onChange={() => setUseFreePlay?.(prev => !prev)} />
                        <span>
                            Use your freeplay balance of ${formatNumbers(user?.freePlay)}
                        </span>
                    </label>
                )}

                <div className="sq-slip__actions">
                    <button type="button" className="sq-slip__btn sq-slip__btn--back" onClick={chooseMore}>
                        Choose More
                    </button>
                    <button
                        type="button"
                        className="sq-slip__btn sq-slip__btn--confirm"
                        onClick={(e) => handleConfirmBet(e, true)}
                        disabled={isSubmitting}
                    >
                        Confirm Bet
                    </button>
                </div>

                {shouldShowDelayedMessage && (
                    <div className="delayed-message-marekts">
                        <span className='font-message-delayed'>*This bet may take a little longer to grade.</span>
                        <span className='font-message-delayed'>Thank you for understanding!</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StraightBetsResponsive